import APIBase from "./apiBase";
export interface CreateRuleDTO {
  messageId?: string;
  scopeTargetType: "global" | "user" | "inbox";
  scopeTargetId: string | null;
  ruleAction: "challenge" | "allow" | "filter";
  extraSourceOption?: null | "sender" | "domain" | "subject";
  category?: string;
  selectedHeaders?: { [key: string]: boolean };
  patterns?: RulePattern[];
}
export interface Rule {
  id: string;
  scopeTargetType: "global" | "user" | "inbox";
  scopeTargetId: string | null;
  ruleAction: "challenge" | "allow" | "filter";
  patterns: RulePattern[];
  status: "active" | "draft" | "disabled";
  sourceType: RuleSourceType;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  scopeTargetDetails?: {
    firstName: string;
    lastName: string;
    email: string;
  };
}

export interface MessageAction {
  id: string;
  threadId: string | null;
  actionType: string;
  mode: string;
  selectedRuleId: string;
  matchedRuleIds: string[];
  challengeId: string | null;
  createdAt: string;
  appliedAt: string | null;
  message: {
    id: string;
    subject: string;
    from: [{ name: string; local: string; domain: string; address: string }];
    timestamp: string;
    inbox: {
      id: string;
      email: string;
    };
  };
}

export interface RuleMatchesResponse {
  ruleId: string;
  messages: MessageAction[];
}

export interface RuleSelectQuery {
  includeGlobal?: boolean;
  targetType?: "user" | "inbox";
  targetId?: string;
  userId?: string;
}

export enum RuleSourceType {
  CAP = "cap",
  INBOX_LABEL = "inboxLabel",
  DASHBOARD = "dashboard",
  SENT_MESSAGE = "sentMessage",
  SENT_MESSAGE_INITIAL_SYNC = "sentMessageInitialSync",
  /** Allow-all for inbox domain after sent sync (Google Workspace / corporate, not consumer Gmail). */
  ONBOARDING_CORPORATE_DOMAIN = "onboardingCorporateDomain",
  IMPORT = "import",
  SYSTEM_TRANSACTIONAL_MAIL = "systemTransactionalMail",
}

export enum RulePatternTarget {
  HEADER_KEY_EXISTS = "header-key-exists",
  SENDER_DOMAIN = "sender-domain",
  SENDER_ADDRESS = "sender-address",
  SUBJECT = "subject",
  RECIPIENT_DOMAIN = "recipient-domain",
  RECIPIENT_ADDRESS = "recipient-address",
}
export interface RulePattern {
  target: RulePatternTarget;
  value: string;
}

export const createRule = async (ruleData: CreateRuleDTO): Promise<Rule> => {
  const { data } = await APIBase.post(`/rules`, ruleData);
  return data;
};
export const findAll = async (query: RuleSelectQuery): Promise<Rule[]> => {
  const { data } = await APIBase.get(`/rules`, { params: query });
  return data;
};

export const deleteRule = async (ruleId: string): Promise<void> => {
  const { data } = await APIBase.delete(`/rules/${ruleId}`);
  return data;
};

export const getRule = async (ruleId: string): Promise<Rule> => {
  const { data } = await APIBase.get(`/rules/${ruleId}`);
  return data;
};

export const exportRules = async (): Promise<void> => {
  const response = await APIBase.get(`/rules/export`, {
    responseType: "blob",
  });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", "rules-export.json");
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const importRules = async (
  file: File,
  format?: "gated",
): Promise<any> => {
  const formData = new FormData();
  formData.append("file", file);
  const { data } = await APIBase.post(
    `/rules/import${format ? `?format=${format}` : ""}`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return data;
};

export const deleteAllRules = async (): Promise<any> => {
  const { data } = await APIBase.post(`/rules/deleteAll`);
  return data;
};

export const getRuleMatches = async (
  ruleId: string,
): Promise<RuleMatchesResponse> => {
  const { data } = await APIBase.get(`/messages/ruleMatches`, {
    params: { ruleId },
  });
  return data;
};
